import { TimeFrames } from '@shared/types/frontend/searchHome';
import { useRouter } from 'next/router';
import { memo, useCallback, useMemo, useState } from 'react';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { NormieHeaderRow, NormieRowType } from '@shared/types/frontend/tables';
import { WRAPPED_SOL, ZERO_ADDRESS } from '@shared/utils/config/networks';
import { useSession } from '@/contexts/LitSessionProvider';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { useRecentToken } from '@/hooks/saved/useRecentToken';
import classNames from '@/lib/misc/classNames';
import { haveDetails } from '@/lib/asset/nativeAddressHelper';
import DTTopTokenModal from './DTTopTokenModal';
import NormieTableRow from './NormieTableRow';
import TopMobileResults from '../../TopMobileResults';
import { getNetworkLogo } from '@/lib/misc/tokenImageHelper';
const RowRenderer = memo(({
  index,
  style,
  data,
  timeFrame,
  resultsToUse
}: {
  index: number;
  style: React.CSSProperties;
  data: any;
  timeFrame: TimeFrames;
  resultsToUse: any;
}) => {
  if (resultsToUse[index].isTop) {
    return <div style={style}>
          <TopMobileResults searchResult={resultsToUse[index]} timeFrame={timeFrame} onTokenClick={null} />
        </div>;
  }
  return <NormieTableRow index={index} style={style} data={data} />;
});
RowRenderer.displayName = 'RowRenderer';
const NormieTable = ({
  resultsToUse,
  setKeyword,
  timeFrame,
  customHandler,
  isSearchResults,
  className
}: {
  resultsToUse: any;
  setKeyword: (_: string) => void;
  timeFrame: TimeFrames;
  customHandler?: (_: any) => void;
  className?: string;
  isSearchResults?: boolean;
  scrollViewClassName?: string;
}) => {
  const router = useRouter();
  const {
    PKP
  } = useSession();
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    handleSaveRecent
  } = useRecentToken();
  const [isModalOpen, setIsOpenModal] = useState(false);
  const [selectedTokenDetails, setSelectedTokenDetails] = useState(null);
  function toggleModal() {
    setIsOpenModal(!isModalOpen);
    setSelectedTokenDetails(null);
  }
  const handleNavigation = useCallback(tokenItem => {
    router.push(`/asset?address=${tokenItem?.address}&networkId=${tokenItem?.networkId}&tradeSide=buy`);
  }, [router]);
  const _handleOnTokenSelect = useCallback(async searchResult => {
    const tokenItem = searchResult?.token;
    if (!selectedTokenDetails && haveDetails(tokenItem)) {
      setSelectedTokenDetails(searchResult);
      setIsOpenModal(true);
      return;
    }
    setKeyword('');
    if (!customHandler) {
      LogToMixpanel({
        pkp: PKP,
        eventName: EVENTS.search.clickToken,
        category: CATEGORIES.search,
        metaData: {
          tokenAddress: tokenItem?.address,
          networkId: tokenItem?.networkId,
          source: 'desktop'
        }
      });
      if (isSearchResults) {
        await handleSaveRecent({
          tokenData: {
            address: tokenItem?.address,
            networkId: tokenItem?.networkId,
            imageLargeUrl: tokenItem?.imageLargeUrl,
            symbol: tokenItem?.symbol,
            token: tokenItem,
            timeStamp: Date.now()
          }
        });
      }
      router.push(`/asset?address=${tokenItem?.address}&networkId=${tokenItem?.networkId}&tradeSide=buy`);
    } else {
      const _token = {
        ...tokenItem,
        imageLargeUrl: tokenItem?.info?.imageThumbUrl
      };
      customHandler(_token);
    }
  }, [selectedTokenDetails, customHandler, handleNavigation, isSearchResults]);
  const tablesRows = useMemo(() => {
    return resultsToUse?.map(result => {
      if (result.type === NormieRowType.HEADER) {
        return result as NormieHeaderRow;
      }
      const tokenItem = result?.token;
      if (!tokenItem) {
        return null;
      }
      return {
        type: NormieRowType.ROW,
        data: result,
        isTop: result.isTop,
        extraData: {
          tokenItem,
          isMultiToken: haveDetails(tokenItem),
          tokenPriceUsd: result?.priceUSD ? result?.priceUSD : result?.prices?.length > 0 ? result?.prices[0]?.priceUsd : 0,
          change: parseFloat(result?.[`change${timeFrame}`] ?? result?.tokenInfo?.results[0]?.[`change${timeFrame}`]),
          marketCap: result?.marketCap ?? result?.tokenInfo?.results[0]?.marketCap,
          tokenImg: tokenItem.address == ZERO_ADDRESS || tokenItem.address == WRAPPED_SOL ? getNetworkLogo(Array.isArray(tokenItem.networkId) ? tokenItem.networkId[tokenItem.networkId.length - 1] : tokenItem.networkId) : tokenItem.info?.imageThumbUrl || tokenItem.imageSmallUrl || tokenItem.info?.imageLargeUrl
        }
      };
    });
  }, [resultsToUse, timeFrame]);
  const listData = useMemo(() => ({
    items: tablesRows,
    onTokenSelect: _handleOnTokenSelect,
    customHandler
  }), [tablesRows, _handleOnTokenSelect, customHandler]);
  return <div className={classNames('flex-1', className)} data-sentry-component="NormieTable" data-sentry-source-file="NormieTable.tsx">
      {resultsToUse && resultsToUse.length > 0 && <AutoSizer>
          {({
        height,
        width
      }) => <List key={`${resultsToUse?.map(r => r.token?.address)?.join('-')}`} height={height} width={width} itemCount={resultsToUse.length} itemSize={index => {
        return resultsToUse[index].type === NormieRowType.HEADER ? (resultsToUse[index] as NormieHeaderRow).height : 80;
      }} itemData={listData} style={{
        overflowX: 'hidden'
      }} className='no-scrollbar'>
              {props => <RowRenderer {...props} timeFrame={timeFrame} resultsToUse={resultsToUse} />}
            </List>}
        </AutoSizer>}
      {selectedTokenDetails && isModalOpen && <DTTopTokenModal onTokenClick={() => _handleOnTokenSelect(selectedTokenDetails)} isModalOpen={isModalOpen} toggleModal={toggleModal} token={selectedTokenDetails} change={parseFloat(selectedTokenDetails?.[`change${timeFrame}`] ?? selectedTokenDetails?.tokenInfo?.results[0]?.[`change${timeFrame}`])} />}
    </div>;
};
export default NormieTable;